import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { getPartners, Partner } from '@yaak/components/services/api/api'
import appStyle from '../../style.less'
import { useNavigate } from 'react-router-dom'
import ProgressBar from '@yaak/components/src/ProgressBar'
import SearchBar from '@yaak/components/src/SearchBar/SearchBar'
import style from './style.less'
import Typography from '@yaak/components/src/Typography'
import { TypographyTypes } from '@yaak/components/src/Typography/Typography'
import Divider from '@yaak/components/src/Divider'
import Grid from '@yaak/components/src/Grid'
import { transpose } from '../../helpers/common'
import Empty from '@yaak/components/src/Empty'
import {
  ToastContext,
  ToastContextType,
} from '@yaak/components/context/toastContext'
import {
  GridTextElement,
  GridSuccessCancelIconElement,
} from '@yaak/components/src/Grid/GridElements'

const HEADERS = ['Name', 'Sim']

const getRows = (partners: Partner[]) =>
  partners?.map((partner) => {
    const row = []
    row.push(
      GridTextElement({
        text: partner.name,
      })
    )
    row.push(
      GridSuccessCancelIconElement({
        condition: partner.simEnabled,
        positive: 'Sim-enabled',
        negative: 'Not sim-enabled',
      })
    )
    return row
  })

const getMappedPartners = (partners: Partner[]) => {
  const columns = transpose(getRows(partners))
  columns.map((row: any, i: number) =>
    HEADERS.map((header, k) => i === k && row.unshift(header))
  )
  return {
    options: {
      fixedColumns: 1,
    },
    rows: columns,
  }
}

interface ModuleConfigOverviewProps {
  token: string
}

const ModuleConfigOverview: React.FunctionComponent<
  ModuleConfigOverviewProps
> = ({ token }) => {
  const { setShowToast } = useContext(ToastContext) as ToastContextType

  const [loading, setLoading] = useState(true)
  const [partners, setPartners] = useState<Partner[]>([])
  const [searchQuery, setSearchQuery] = useState('')

  const navigate = useNavigate()

  const mappedData = useMemo(() => {
    return getMappedPartners(partners)
  }, [partners])

  useEffect(() => {
    const fetchPartners = async () => {
      const result = await getPartners({
        token,
        onAlert: setShowToast,
        searchQuery,
      })
      setLoading(false)
      setPartners(result)
    }

    if (searchQuery !== null && token != null) {
      const timeout = setTimeout(() => {
        token && fetchPartners()
      }, 500)

      return () => clearTimeout(timeout)
    }
  }, [token, searchQuery])

  const onRowClick = useCallback(
    (rowIndex: number) => {
      navigate(`/simulator/module-config/${partners[rowIndex].id}`)
    },
    [partners]
  )

  return (
    <div className={appStyle.page}>
      <Typography type={TypographyTypes.headline}>
        {'Partner Modules'}
      </Typography>
      <Divider />
      {loading ? (
        <ProgressBar />
      ) : (
        <>
          <div className={appStyle.actionBar}>
            <div className={style.searchBox}>
              <SearchBar
                setSearchQuery={setSearchQuery}
                placeholder="Search by partner names and IDs"
                clear={true}
              />
            </div>
          </div>
          {partners?.length > 0 ? (
            <div className={style.tableWrapper}>
              <Grid
                headers={HEADERS}
                data={mappedData}
                onRowClick={onRowClick}
                headerSort={[]}
              />
            </div>
          ) : (
            partners?.length === 0 &&
            (searchQuery ? (
              <Empty
                header={'Looks like no partners match your search criteria.'}
                content={'Try changing your search to see more results.'}
              />
            ) : (
              <Empty
                header={'No partners found.'}
                content={'There are no partners available.'}
              />
            ))
          )}
        </>
      )}
    </div>
  )
}

export default ModuleConfigOverview
